import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Folder } from "lucide-react";

interface MoveFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mediaIds: string[];
}

export const MoveFolderDialog = ({ open, onOpenChange, mediaIds }: MoveFolderDialogProps) => {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: folders } = useQuery({
    queryKey: ['media-folders'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('media_folders')
        .select('*')
        .order('name');
      
      if (error) throw error;
      return data;
    },
  });

  const moveMutation = useMutation({
    mutationFn: async (folderId: string | null) => {
      const { error } = await supabase
        .from('media_library')
        .update({ folder_id: folderId })
        .in('id', mediaIds);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-library'] });
      onOpenChange(false);
      toast({ 
        title: "Success",
        description: `Moved ${mediaIds.length} item(s) to folder` 
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move to Folder</DialogTitle>
        </DialogHeader>

        <RadioGroup value={selectedFolderId || 'none'} onValueChange={setSelectedFolderId}>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="none" id="none" />
            <Label htmlFor="none" className="flex items-center gap-2">
              <Folder className="h-4 w-4" />
              No Folder (Root)
            </Label>
          </div>

          {folders?.map(folder => (
            <div key={folder.id} className="flex items-center space-x-2">
              <RadioGroupItem value={folder.id} id={folder.id} />
              <Label htmlFor={folder.id} className="flex items-center gap-2">
                <Folder className="h-4 w-4" />
                {folder.name}
              </Label>
            </div>
          ))}
        </RadioGroup>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => moveMutation.mutate(selectedFolderId === 'none' ? null : selectedFolderId)}
            disabled={moveMutation.isPending}
          >
            Move
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
